/**
 * Lightbox Suport
 *
 * @see https://www.npmjs.com/package/basiclightbox
 * @see https://github.com/electerious/basicLightbox
 *
 * @since 1.0.0
 * @since 1.0.2 drops jQuery dependency and Colorbox
 */

import * as basicLightbox from 'basiclightbox';
import { breakpoints } from './variables';

/**
 * Add a classname to an element, given that it contains an <img>.
 *
 * @since 1.0.0
 * @since 1.0.1 Stopped using jQuery, now only applies to a single element
 *
 * @param {object} element      Element node
 * @param {String} className 	Class to add to element if direct child is an img or picture element.
 */
export function addImageLinkClass(element, className) {
	const image = element.querySelector('img');
	if (image) {
		element.classList.add(className);
	}
	return image;
}

/**
 * Generate the close <button> for basicLightBox
 *
 * @since 1.0.0
 *
 * @param {number} index	the current element's index
 */
export function getLightboxCloseButton(index) {
	const button = `<button id="lightbox-${index}" tabindex="1" class="basicLightbox__close no-button-style"><span class="screen-reader-text">Close</span><span class="dashicons dashicons-no-alt"></span></button>`;
	return button;
}

/**
 * Launches links to valid image formats into a lightbox instead of opening within window
 *
 * @see https://github.com/electerious/basicLightbox
 *
 * @since  1.0.1 Uses basicLightbox instead of Colorbox
 */
function initImageLightBox() {
	/**
	 * Query for anchor elements linking to supported image types.
	 */
	const imageEls = document.querySelectorAll('a[href*=".jpg"],a[href*=".jpeg"],a[href*=".gif"],a[href*=".png"],a[href*=".svg"]');

	imageEls.forEach((elem, index) => {
		// Check if element parent has gallery class, if so return null.
		if (elem.parentNode.classList.contains('gallery-icon')) {
			return;
		}

		/**
		 * Add enlarge class to link if it contains an image.
		 */
		const image = addImageLinkClass(elem, 'enlarge');

		/**
		 * Define our src and alt attributes to use in the <img /> tag
		 */
		const attrSrc = elem.getAttribute('href');
		const attrAlt = image ? image.getAttribute('alt') : '';

		/**
		 * Fetch the markup for the lightbox close button
		 */
		const lightboxCloseButton = getLightboxCloseButton(index);

		/**
		 * Create <img /> to render in the lightbox.
		 */
		const html = basicLightbox.create(
			`${lightboxCloseButton}
			<div class="basicLightbox__image">
				<img src="${attrSrc}" alt="${attrAlt}" />
			</div>`,
			{
				afterShow: () => { document.getElementById(`lightbox-${index}`).onclick = function () { html.close(); }; },
			},
		);

		let start, end, delta;

		elem.onmousedown = function() {
			start = new Date();
		}

		elem.onmouseup = function() {
		 	end = new Date();
	        delta = (end - start) / 1000.0;
		}

		/**
		 * Launch the ligtbox on link click and prevent normal link behavior
		 */
		elem.addEventListener('click', (e) => {
			e.preventDefault();

			if( delta > .2) {
				return;
			}

			html.show();
		});
	});
}

/**
 * Launches lightbox gallery using WordPress Default gallery options.
 *
 * @see https://github.com/electerious/basicLightbox
 *
 * @since  1.0.1 Uses basicLightbox instead of Colorbox
 */
function initImageGallery() {
	const getSrc = (elem) => elem.querySelectorAll('a')[0].getAttribute('href');
	const getPrev = (elem) => elem.previousElementSibling;
	const getNext = (elem) => elem.nextElementSibling;
	const getAll = document.getElementsByClassName('gallery-item');

	const open = function (elem) {
		const init = (instance) => {
			/**
			 * Remove Current src and set appropriate.
			 */
			instance.element().querySelector('img').src = '';
			instance.element().querySelector('img').src = getSrc(elem);

			const prev = instance.element().querySelector('#prev');
			const next = instance.element().querySelector('#next');

			prev.onclick = () => {
				elem = getPrev(elem);

				// If no previous found show last one.
				if (!elem) {
					elem = getAll[getAll.length - 1];
				}
				init(instance);
			};

			next.onclick = () => {
				elem = getNext(elem);

				// If no next element found show first one.
				if (!elem) {
					// for eslint crying about destructuring...
					const [first] = getAll;
					elem = first;
				}
				init(instance);
			};
		};

		/**
		 * Fetch the markup for the lightbox close button
		 */
		const lightboxCloseButton = getLightboxCloseButton('close');

		/**
		 * Create <img /> to render in the lightbox, set next an prev buttons
		 * Close lightbox after open.
		 */
		const html = basicLightbox.create(`${lightboxCloseButton} <img>`, {
			beforePlaceholder: '<button id="prev" class="prev no-button-style"><span class="screen-reader-text">Previous</span><span class="dashicons dashicons-arrow-left-alt2"></span></button>',
			afterPlaceholder: '<button id="next" class="next no-button-style"><span class="screen-reader-text">Next</span><span class="dashicons dashicons-arrow-right-alt2"></span></button>',
			beforeShow: init,
			afterShow: () => { document.getElementById('lightbox-close').onclick = function () { html.close(); }; },
		});

		html.show();
	};

	document.querySelectorAll('.gallery-item').forEach((elem) => {
		elem.onclick = function (e) {
			// Prevent default link behavior
			e.preventDefault();
			open(elem);
		};
	});
}

/**
 * Display YouTube, Vimeo or Wistia videos into a lightbox (via ColorBox)
 * if the viewport is above a certain size and the device is not iOS.
 *
 * @see http://www.jacklmoore.com/colorbox/
 *
 * @since 1.0.0
 * @since 1.0.1 Using regex match to check video service and to pull out the video's ID.
 * @since 1.0.2 Uses basicLightbox instead of Colorbox
 *
 */
function initVideoLightbox() {
	/**
	 * Regex patterns for matching various video URLs from popular services.
	 */
	const youtubeRegex = /(?:youtube\.com\/\S*(?:(?:\/e(?:mbed))?\/|watch\/?\?(?:\S*?&?v=))|youtu\.be\/)([a-zA-Z0-9_-]{6,11})/;
	const vimeoRegex = /(http|https)?:\/\/(www\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/([^/]*)\/videos\/|)(\d+)(?:|\/\?)/;
	const wistiaRegex = /(?:https?:\/\/(.+)?(wistia.com|wi.st)\/(medias|embed)\/)(.*)/;
	const videoEls = document.querySelectorAll('[href*="youtube.com"],[href*="youtu.be"],[href*="vimeo.com"],[href*="wistia.com"],[href*=".mp4"]');

	videoEls.forEach((elem, index) => {
		const href = elem.getAttribute('href');
		let attrSrc = false;
		let iframe = true;

		/**
		 * Add play-btn class to link if it contains an image.
		 */
		addImageLinkClass(elem, 'play-btn');

		/**
		 * Check if the video link is an MP4
		 */
		if (href.indexOf('.mp4') >= 0) {
			attrSrc = href;
			iframe = false;
		}

		/**
		 * Check if there is a match for YouTube that isn't a channel page
		 */
		if (!attrSrc) {
			const youTubeMatches = href.match(youtubeRegex);
			if (youTubeMatches !== null && href.match(/youtube.com\/user|youtube.com\/channel/) === null) {
				attrSrc = `https://www.youtube.com/embed/${youTubeMatches[1]}?autoplay=1&hd=1&rel=0`;
			}
		}

		/**
		 * Check if there is a match for Vimeo
		 */
		if (!attrSrc) {
			const vimeoMatches = href.match(vimeoRegex);
			if (vimeoMatches !== null) {
				attrSrc = `https://player.vimeo.com/video/${vimeoMatches[4]}?autoplay=1`;
			}
		}

		/**
		 * Check if there is a match for Wistia
		 */
		if (!attrSrc) {
			const wistiaMatches = href.match(wistiaRegex);
			if (wistiaMatches !== null) {
				attrSrc = `https://fast.wistia.net/embed/iframe/${wistiaMatches[4]}?autoplay=1`;
			}
		}

		/**
		 * Only proceed if we have a valid src attribute for an iframe
		 */
		if (!attrSrc) {
			return;
		}

		/**
		 * Set lightbox width based on viewport size.
		 */
		let attrWidth = 853;
		if (breakpoints.lg.aboveMin()) {
			attrWidth = 980;
		} else if (breakpoints.xs.belowMax()) {
			attrWidth = 500;
		} else if (breakpoints.sm.belowMax()) {
			attrWidth = 650;
		}

		/**
		 * Set the lightbox height based on a 16:9 aspect ratio.
		 */
		const attrHeight = attrWidth * (9 / 16);

		/**
		 * Fetch the markup for the lightbox close button
		 */
		const lightboxCloseButton = getLightboxCloseButton(index);

		/**
		 * Determine if the video should be renderd in an <iframe> or <video> element
		 * MP4 need to be rendered in a <video> tag to prevent IE11 from prompting to open/download
		 *
		 * @see https://github.com/emagineusa/embark/issues/24
		 */
		let markup = '';
		if (iframe) {
			markup = `<iframe width="${attrWidth}" height="${attrHeight}" src="${attrSrc}" frameborder="0" allowfullscreen></iframe>`;
		} else {
			markup = `<video controls src="${attrSrc}" width="${attrWidth}"></video>`;
		}

		/**
		 * Create <iframe> to render in the lightbox.
		 */
		const html = basicLightbox.create(
			`${lightboxCloseButton} ${markup}`,
			{
				afterShow: () => { document.getElementById(`lightbox-${index}`).onclick = function () { html.close(); }; if (!iframe) { document.querySelector('.basicLightbox video').play(); } },
			},
		);

		/**
		 * Open the video in a lightbox when the link is clicked.
		 */
		elem.onclick = function (e) {
			// Prevent default link behavior
			e.preventDefault();
			html.show();
		};
	});
}

/**
 * Run our lightbox functions once the DOM has been completely loaded.
 */
document.addEventListener('DOMContentLoaded', () => {
	initImageLightBox();
	initVideoLightbox();
	initImageGallery();
});
